import { useState, useEffect, useCallback } from 'react';
import styles from './nav.module.css';

const PHONE = '972504647878';
const WA_MSG = encodeURIComponent('היי רומי, הגעתי מהאתר שלך ואשמח לשמוע עוד..');

const NAV_ITEMS = [
  { label: 'מי אני',       targetId: 'story' },
  { label: 'לכל הקורסים',  targetId: 'courses' },
  { label: 'מחשבון שכר פוטנציאלי', targetId: 'calculator' },
  { label: 'שאלות נפוצות', targetId: 'faq' },
  {
    label: 'צרי קשר',
    external: true,
    href: `https://wa.me/${PHONE}?text=${WA_MSG}`,
  },
];

function Navbar() {
  const [isOpen, setIsOpen]     = useState(false);
  const [visible, setVisible]   = useState(false);

  const handleScroll = useCallback(() => {
    setVisible(window.scrollY > 300);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && isOpen) closeMenu(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen]);

  const openMenu = () => {
    setIsOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeMenu = () => {
    setIsOpen(false);
    document.body.style.overflow = '';
  };

  const scrollToSection = (targetId) => {
    closeMenu();
    setTimeout(() => {
      const el = document.getElementById(targetId);
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY - 80;
      window.scrollTo({ top, behavior: 'smooth' });
    }, 700);
  };

  const navVisible = visible || isOpen;

  return (
    <>
      {/* Fixed Top Bar */}
      <nav
        className={`${styles.topBar} ${navVisible ? styles.navVisible : ''}`}
        dir="rtl"
      >
        <button className={styles.menuButton} onClick={openMenu} aria-label="פתח תפריט">
          <span className={styles.menuLabel}>תפריט</span>
          <span className={styles.menuIcon}>
            <span className={styles.menuLine} />
            <span className={styles.menuLine} />
          </span>
        </button>
      </nav>

      {/* Full-screen Menu */}
      <div
        className={`${styles.fullscreenMenu} ${isOpen ? styles.menuOpen : ''}`}
      >
        {/* Frame lines */}
        {['frameTop', 'frameBottom', 'frameLeft', 'frameRight'].map((cls, i) => (
          <div
            key={cls}
            className={`${styles[cls]} ${styles.frameLine} ${isOpen ? styles.frameLineVisible : ''}`}
            style={{ transitionDelay: isOpen ? `${0.3 + i * 0.05}s` : '0s' }}
          />
        ))}

        <div className={styles.menuContent}>

          {/* Nav links */}
          <div className={styles.navSection}>
            <nav className={styles.navLinks}>
              {NAV_ITEMS.map((item, i) =>
                item.external ? (
                  <a
                    key={i}
                    href={item.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`${styles.navLink} ${styles.navLinkAnimated} ${isOpen ? styles.navLinkVisible : ''}`}
                    onClick={closeMenu}
                    style={{ transitionDelay: isOpen ? `${0.28 + i * 0.055}s` : '0s' }}
                  >
                    <span className={styles.navLabel}>{item.label}</span>
                    <span className={styles.navNumber}>({i + 1})</span>
                  </a>
                ) : (
                  <button
                    key={i}
                    onClick={() => scrollToSection(item.targetId)}
                    className={`${styles.navLink} ${styles.navLinkAnimated} ${isOpen ? styles.navLinkVisible : ''}`}
                    style={{ transitionDelay: isOpen ? `${0.28 + i * 0.055}s` : '0s' }}
                  >
                    <span className={styles.navLabel}>{item.label}</span>
                    <span className={styles.navNumber}>({i + 1})</span>
                  </button>
                )
              )}
            </nav>
          </div>

          {/* Contact info */}
          <div
            className={`${styles.contactSection} ${styles.contactAnimated} ${isOpen ? styles.contactVisible : ''}`}
          >
            <div className={styles.contactGroup}>
              <span className={styles.contactLabel}>טלפון</span>
              <a href={`tel:+${PHONE}`} className={styles.contactValue}>
                <svg className={styles.contactIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6A19.79 19.79 0 012.12 4.18 2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" />
                </svg>
                050-464-7878
              </a>
            </div>

            <div className={styles.contactGroup}>
              <span className={styles.contactLabel}>וואטסאפ</span>
              <a
                href={`https://wa.me/${PHONE}?text=${WA_MSG}`}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.contactValue}
              >
                <svg className={styles.contactIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z" />
                </svg>
                שלחי הודעה
              </a>
            </div>

            <div className={styles.contactGroup}>
              <span className={styles.contactLabel}>עקבי אחריי</span>
              <div className={styles.socialLinks}>
                <a
                  href="https://www.instagram.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className={styles.socialLink}
                >
                  <svg className={styles.socialIcon} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                    <circle cx="12" cy="12" r="5" />
                    <circle cx="17.5" cy="6.5" r="1.5" fill="currentColor" stroke="none" />
                  </svg>
                  אינסטגרם
                </a>
              </div>
            </div>
          </div>

          {/* Legal / footer row */}
          <div
            className={`${styles.legalSection} ${styles.legalAnimated} ${isOpen ? styles.legalVisible : ''}`}
          />

          {/* Close button */}
          <button
            className={`${styles.closeButton} ${styles.closeAnimated} ${isOpen ? styles.closeVisible : ''}`}
            onClick={closeMenu}
            aria-label="סגור תפריט"
          >
            סגור
          </button>
        </div>
      </div>
    </>
  );
}

export default Navbar;
